import Layout from '../../../components/feature/Layout';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { fetchQuestionById, fetchSimilarQuestions } from '../../../repositories/questions';
import { fetchQuestionComments, createQuestionComment } from '../../../repositories/questionComments';
import type { Question } from '../../../types/questions';
import type { QuestionComment } from '../../../types/questionComments';

export default function QuestionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [question, setQuestion] = useState<Question | null>(null);
  const [similarQuestions, setSimilarQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // コメント関連の state
  const [comments, setComments] = useState<QuestionComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);

  // コメント投稿フォームの state
  const [commentForm, setCommentForm] = useState({
    author_name: '',
    body_raw: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    async function loadQuestion() {
      if (!id) {
        setError('指定された相談が見つかりませんでした。');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await fetchQuestionById(id);

        if (!data) {
          setError('指定された相談が見つかりませんでした。');
        } else if (data.status !== 'approved' || !data.is_published) {
          setError('この相談は現在公開されていません。');
        } else {
          setQuestion(data);
          
          // 似た相談を取得
          const similar = await fetchSimilarQuestions(
            data.id,
            data.subject,
            data.grade,
            3
          );
          setSimilarQuestions(similar);

          // コメントを取得
          await loadComments(data.id);
        }
      } catch (err) {
        console.error('Failed to load question:', err);
        setError('相談の読み込みに失敗しました。');
      } finally {
        setLoading(false);
      }
    }

    loadQuestion();
  }, [id]);

  // コメント一覧を取得
  const loadComments = async (questionId: string) => {
    try {
      setCommentsLoading(true);
      setCommentsError(null);
      const data = await fetchQuestionComments(questionId);
      setComments(data);
    } catch (err) {
      console.error('Failed to load comments:', err);
      setCommentsError('コメントの読み込みに失敗しました。');
    } finally {
      setCommentsLoading(false);
    }
  };

  // コメント投稿
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id || !question) return;

    // バリデーション
    if (!commentForm.author_name.trim()) {
      setSubmitError('ニックネームを入力してください。');
      return;
    }

    if (!commentForm.body_raw.trim()) {
      setSubmitError('コメント本文を入力してください。');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      await createQuestionComment({
        question_id: id,
        author_name: commentForm.author_name.trim(),
        body_raw: commentForm.body_raw.trim(),
      });

      // フォームをクリア
      setCommentForm({
        author_name: '',
        body_raw: '',
      });

      // 成功メッセージを表示
      setSubmitSuccess(true);

      // 3秒後に成功メッセージを消す
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Failed to submit comment:', err);
      setSubmitError('コメントの投稿に失敗しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* パンくずリスト */}
        <nav className="mb-6 text-sm text-gray-600">
          <Link to="/" className="hover:text-teal-600 cursor-pointer">
            ホーム
          </Link>
          <span className="mx-2">/</span>
          <Link to="/questions" className="hover:text-teal-600 cursor-pointer">
            学習相談
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900">詳細</span>
        </nav>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600">読み込み中…</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-center mb-4">{error}</p>
            <div className="text-center">
              <Link
                to="/questions"
                className="inline-block px-6 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                学習相談一覧に戻る
              </Link>
            </div>
          </div>
        )}

        {!loading && !error && question && (
          <>
            {/* ピックアップバッジ */}
            {question.is_featured && (
              <div className="mb-4">
                <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full whitespace-nowrap">
                  <i className="ri-star-fill mr-1"></i>
                  ピックアップ
                </span>
              </div>
            )}

            {/* 質問本文 */}
            <section className="mb-8">
              <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {question.subject && (
                    <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full whitespace-nowrap">
                      {question.subject}
                    </span>
                  )}
                  {question.grade && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full whitespace-nowrap">
                      {question.grade}
                    </span>
                  )}
                  {question.role && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full whitespace-nowrap">
                      {question.role}
                    </span>
                  )}
                  <span className="text-sm text-gray-500">
                    <i className="ri-calendar-line mr-1"></i>
                    {formatDate(question.created_at)}
                  </span>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-6">
                  {question.title}
                </h1>

                <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {question.body_public || question.body_raw}
                </div>

                {/* タグ */}
                {question.tags && question.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-gray-200">
                    {question.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* 管理者の回答 */}
            {question.answer && (
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-chat-check-line mr-2 text-teal-600"></i>
                  回答
                </h2>
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-admin-line text-xl text-teal-600"></i>
                    <span className="font-semibold text-gray-900">
                      {question.answered_by || '管理者'}
                    </span>
                  </div>
                  <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {question.answer}
                  </div>
                </div>
              </section>
            )}

            {/* コメント一覧 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-chat-3-line mr-2 text-teal-600"></i>
                コメント
                {comments.length > 0 && (
                  <span className="ml-2 text-sm text-gray-500">（{comments.length}件）</span>
                )}
              </h2>

              {commentsLoading && (
                <div className="text-center py-8 text-gray-600">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                  <p className="mt-2 text-sm">コメントを読み込み中…</p>
                </div>
              )}

              {commentsError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-600">{commentsError}</p>
                </div>
              )}

              {!commentsLoading && !commentsError && comments.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                  <p className="text-gray-600">まだコメントはありません。</p>
                  <p className="text-sm text-gray-500 mt-2">
                    最初のコメントを投稿してみませんか？
                  </p>
                </div>
              )}

              {!commentsLoading && !commentsError && comments.length > 0 && (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="bg-white border border-gray-200 rounded-lg p-6"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                          <i className="ri-user-line text-teal-600"></i>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {comment.author_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(comment.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {comment.body_public}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* コメント投稿フォーム */}
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                コメントを投稿する
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                管理者の承認後に公開されます。
              </p>

              {submitSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                    <p className="text-sm text-green-700">
                      コメントを受け付けました。公開までしばらくお待ちください。
                    </p>
                  </div>
                </div>
              )}

              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              <form onSubmit={handleSubmitComment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ニックネーム<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={commentForm.author_name}
                    onChange={(e) =>
                      setCommentForm({ ...commentForm, author_name: e.target.value })
                    }
                    placeholder="例：たろう"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    コメント本文<span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={commentForm.body_raw}
                    onChange={(e) =>
                      setCommentForm({ ...commentForm, body_raw: e.target.value })
                    }
                    rows={5}
                    placeholder="コメントを入力してください"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    disabled={submitting}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? '投稿中…' : 'コメントを投稿'}
                  </button>
                </div>
              </form>
            </section>

            {/* 似た相談 */}
            {similarQuestions.length > 0 && (
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-file-list-line mr-2 text-teal-600"></i>
                  似た相談を見る
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {similarQuestions.map((similar) => (
                    <div
                      key={similar.id}
                      onClick={() => navigate(`/questions/${similar.slug || similar.id}`)}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {similar.subject && (
                          <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded whitespace-nowrap">
                            {similar.subject}
                          </span>
                        )}
                        {similar.grade && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded whitespace-nowrap">
                            {similar.grade}
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">
                        {similar.title}
                      </h3>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* このあと、できること */}
            <section className="mb-8">
              <div className="bg-teal-50 border border-teal-100 rounded-lg p-4 md:p-5 space-y-3">
                <h2 className="text-sm font-semibold text-teal-800 mb-3">
                  <i className="ri-lightbulb-line mr-1"></i>
                  このあと、できること
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 似た学習相談を探す */}
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      <i className="ri-search-line mr-1 text-teal-600"></i>
                      似た学習相談を探す
                    </h3>
                    <p className="text-sm text-gray-700 mb-3">
                      一覧ページに戻って、同じ教科・学年の相談を見てみましょう。
                    </p>
                    <button
                      onClick={() => navigate('/questions')}
                      className="w-full px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      相談一覧へ
                    </button>
                  </div>

                  {/* 活用レシピを読む */}
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      <i className="ri-book-open-line mr-1 text-teal-600"></i>
                      活用レシピを読む
                    </h3>
                    <p className="text-sm text-gray-700 mb-3">
                      「活用レシピ」ページでは、具体的な支援の工夫例を紹介しています。
                    </p>
                    <button
                      onClick={() => navigate('/recipes')}
                      className="w-full px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      活用レシピを見る
                    </button>
                  </div>

                  {/* Boratasu でオンライン支援を検討する */}
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      <i className="ri-user-heart-line mr-1 text-teal-600"></i>
                      Boratasu でオンライン支援を検討する
                    </h3>
                    <p className="text-sm text-gray-700 mb-3">
                      オンラインで学習サポーターとつながるサービス「ボラタス」の利用も検討できます。
                    </p>
                    <a
                      href="https://boratasu.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap text-center"
                    >
                      <i className="ri-external-link-line mr-1"></i>
                      Boratasu のサービスを見る
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* ボラタスへの導線 */}
            <section className="mb-8">
              <div className="bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg p-6 text-white">
                <h2 className="text-xl font-bold mb-2 flex items-center">
                  <i className="ri-user-heart-line mr-2"></i>
                  オンラインで学習をサポート
                </h2>
                <p className="mb-4 opacity-90">
                  ボラタスアプリで、ボランティアとつながって一緒に学びませんか？
                </p>
                <a
                  href="https://voluntas.jp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  ボラタスアプリへ
                </a>
              </div>
            </section>

            {/* 一覧に戻るボタン */}
            <div className="text-center">
              <Link
                to="/questions"
                className="inline-block px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                学習相談一覧に戻る
              </Link>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
